/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Workspace } from "@/hooks/useWorkspace";

export type Item = { id: string; name: string; count: number };

export type TagListConfig = {
  table: "tags";
  joinTable: "excerpt_tags";
  joinFk: "tag_id";
  joinSelect: string;
  joinKey: string;
  singular: string;
  emptyHint: string;
};

export const TAGS_CONFIG: TagListConfig = {
  table: "tags",
  joinTable: "excerpt_tags",
  joinFk: "tag_id",
  joinSelect: "id, name, excerpt_tags(count)",
  joinKey: "excerpt_tags",
  singular: "tag",
  emptyHint: "No tags yet. Add tags from an excerpt's editor.",
};

export function useTagCategoryList(active: Workspace | null, cfg: TagListConfig) {
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!active) return;
    setLoading(true);
    const { data, error } = await (supabase as any)
      .from(cfg.table)
      .select(cfg.joinSelect)
      .eq("workspace_id", active.id)
      .order("name");
    if (error) {
      toast.error(error.message);
      setLoading(false);
      return;
    }
    const mapped: Item[] = (data ?? []).map((row: any) => {
      const join = row[cfg.joinKey];
      const count = Array.isArray(join) ? (join[0]?.count ?? 0) : 0;
      return { id: row.id, name: row.name, count };
    });
    setItems(mapped);
    setLoading(false);
  }, [active, cfg.table, cfg.joinSelect, cfg.joinKey]);

  useEffect(() => { load(); }, [load]);

  return { items, loading, load };
}

export async function createTag(
  active: Workspace,
  cfg: TagListConfig,
  name: string,
  existing: Item[],
): Promise<boolean> {
  const trimmed = name.trim();
  if (!trimmed) { toast.error("Name cannot be empty"); return false; }
  if (existing.some((i) => i.name.toLowerCase() === trimmed.toLowerCase())) {
    toast.error(`A ${cfg.singular} with that name already exists`);
    return false;
  }
  const { error } = await (supabase as any)
    .from(cfg.table)
    .insert({ name: trimmed, workspace_id: active.id });
  if (error) { toast.error(error.message); return false; }
  toast.success(`${cfg.singular[0].toUpperCase()}${cfg.singular.slice(1)} created`);
  return true;
}

export async function renameTag(
  cfg: TagListConfig,
  item: Item,
  next: string,
  existing: Item[],
): Promise<boolean> {
  const trimmed = next.trim();
  if (!trimmed) { toast.error("Name cannot be empty"); return false; }
  if (trimmed === item.name) return true;
  if (existing.some((i) => i.id !== item.id && i.name.toLowerCase() === trimmed.toLowerCase())) {
    toast.error(`A ${cfg.singular} with that name already exists`);
    return false;
  }
  const { error } = await (supabase as any)
    .from(cfg.table)
    .update({ name: trimmed })
    .eq("id", item.id);
  if (error) { toast.error(error.message); return false; }
  toast.success("Renamed");
  return true;
}

export async function deleteTag(cfg: TagListConfig, item: Item): Promise<boolean> {
  const { error } = await (supabase as any)
    .from(cfg.table)
    .delete()
    .eq("id", item.id);
  if (error) { toast.error(error.message); return false; }
  toast.success(`${cfg.singular[0].toUpperCase()}${cfg.singular.slice(1)} deleted`);
  return true;
}

export async function mergeTagInto(
  source: Item,
  targetId: string,
): Promise<void> {
  const { data: sourceLinks, error: srcErr } = await supabase
    .from("excerpt_tags")
    .select("excerpt_id")
    .eq("tag_id", source.id);
  if (srcErr) throw srcErr;

  const { data: targetLinks, error: tgtErr } = await supabase
    .from("excerpt_tags")
    .select("excerpt_id")
    .eq("tag_id", targetId);
  if (tgtErr) throw tgtErr;

  const targetExcerptIds = new Set((targetLinks ?? []).map((r) => r.excerpt_id));
  const toInsert = (sourceLinks ?? [])
    .filter((r) => !targetExcerptIds.has(r.excerpt_id))
    .map((r) => ({ excerpt_id: r.excerpt_id, tag_id: targetId }));

  if (toInsert.length) {
    const { error: insErr } = await supabase.from("excerpt_tags").insert(toInsert);
    if (insErr) throw insErr;
  }

  const { error: delLinkErr } = await supabase
    .from("excerpt_tags")
    .delete()
    .eq("tag_id", source.id);
  if (delLinkErr) throw delLinkErr;

  const { error: delErr } = await supabase.from("tags").delete().eq("id", source.id);
  if (delErr) throw delErr;
}
